import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import ResearchDisclaimer from '../../Components/Common/ResearchDisclaimer';
import { 
    Card, 
    CardBody, 
    Col, 
    Container, 
    Row, 
    Table,
    Badge,
    Button,
    Alert,
    Spinner,
    Form,
    FormGroup,
    Label,
    Input
} from 'reactstrap';
import PreviewCardHeader from '../../Components/Common/PreviewCardHeader';
import config from '../../config';
import '../styles/predictions.css';

const PredictionsByDate = () => {
    const { year, month, day } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const type = searchParams.get('type') || 'created'; // 'created' or 'expired'
    const symbol = searchParams.get('symbol') || '';
    const model = searchParams.get('model') || '';
    const timeframe = searchParams.get('timeframe') || '';
    const isCleanMode = searchParams.get('clean') === 'true';
    
    const [predictions, setPredictions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filter states
    const [filterYear, setFilterYear] = useState(year || new Date().getFullYear().toString());
    const [filterMonth, setFilterMonth] = useState(month || (new Date().getMonth() + 1).toString());
    const [filterDay, setFilterDay] = useState(day || new Date().getDate().toString());
    const [filterSymbol, setFilterSymbol] = useState(symbol);
    const [filterModel, setFilterModel] = useState(model);
    const [filterTimeframe, setFilterTimeframe] = useState(timeframe);
    
    // Available options (will be populated from API)
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [availableModels, setAvailableModels] = useState([]);
    const [availableTimeframes, setAvailableTimeframes] = useState([]);

    const selectedDate = `${filterYear}-${filterMonth.padStart(2, '0')}-${filterDay.padStart(2, '0')}`;
    const displayDate = new Date(selectedDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Function to handle prediction row clicks
    const handlePredictionClick = (predictionId, event) => {
        // Check if the clicked element is a link or inside a link
        const target = event.target;
        const isLinkClick = target.closest('a') || target.closest('button') || target.closest('.dropdown');
        
        if (isLinkClick) {
            return; // Don't navigate if clicking on a link or button
        }
        
        const baseUrl = `/prediction/${predictionId}`;
        const url = isCleanMode ? `${baseUrl}?clean=true` : baseUrl;
        
        // Open in new tab with proper window features
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        // Fallback if popup was blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            // Navigate in same window as fallback
            window.location.href = url;
        }
    };

    useEffect(() => {
        // Update URL when date changes
        if (filterYear && filterMonth && filterDay) {
            const params = new URLSearchParams();
            params.set('type', type);
            if (filterSymbol) params.set('symbol', filterSymbol);
            if (filterModel) params.set('model', filterModel);
            if (filterTimeframe) params.set('timeframe', filterTimeframe);
            
            const newUrl = `/predictions-by-date/${filterYear}/${filterMonth}/${filterDay}?${params.toString()}`;
            
            // Only navigate if URL actually changed
            const currentPath = window.location.pathname + window.location.search;
            if (currentPath !== newUrl) {
                navigate(newUrl, { replace: true });
            }
        }
    }, [filterYear, filterMonth, filterDay, type, filterSymbol, filterModel, filterTimeframe, navigate]);

    // Sync URL parameters with state on component mount
    useEffect(() => {
        if (year) setFilterYear(year);
        if (month) setFilterMonth(month);
        if (day) setFilterDay(day);
        if (symbol) setFilterSymbol(symbol);
        if (model) setFilterModel(model);
        if (timeframe) setFilterTimeframe(timeframe);
    }, [year, month, day, symbol, model, timeframe]);

    useEffect(() => {
        fetchAvailableOptions();
    }, []);

    useEffect(() => {
        fetchPredictionsByDate();
    }, [filterYear, filterMonth, filterDay, type, filterSymbol, filterModel, filterTimeframe]);

    const fetchAvailableOptions = async () => {
        try {
            // Fetch symbols from the supported assets API
            const symbolsResponse = await fetch(`${config.api.API_URL}/api/ai-predictions/supported-assets`, {
                credentials: 'include'
            });
            
            let symbolsList = ['BTC', 'ETH', 'XRP', 'SOL', 'PAXG', 'AVAX', 'ADA', 'ARB', 'LINK', 'UNI', 'DOGE', 'SPY', 'TQQQ', 'GUSH', 'TMF', 'TNA', 'UGL']; // fallback
            
            if (symbolsResponse.ok) {
                const symbolsData = await symbolsResponse.json();
                // Handle both response formats: direct data or wrapped in success/data structure
                let symbolsObj = null;
                if (symbolsData.data && symbolsData.data.symbols) {
                  // Format: { success: true, data: { symbols: {...} } }
                  symbolsObj = symbolsData.data.symbols;
                } else if (symbolsData.symbols) {
                  // Format: { symbols: {...} }
                  symbolsObj = symbolsData.symbols;
                }
                
                if (symbolsObj && typeof symbolsObj === 'object') {
                  symbolsList = Object.keys(symbolsObj);
                }
            }
            
            setAvailableSymbols(symbolsList);
            
            // Fetch other options
            const response = await fetch(`${config.api.API_URL}/api/predictions/options`, {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                
                // Use database-driven models
                setAvailableModels(data.models || []);
                
                // Use database-driven timeframes but ensure proper format
                const timeframes = data.timeframes || [];
                setAvailableTimeframes(timeframes);
            } else {
                throw new Error('Failed to fetch options');
            }
        } catch (error) {
            console.error('Error fetching available options:', error);
            // Set fallback values if API fails
            setAvailableSymbols(['BTC', 'ETH', 'XRP', 'SOL', 'PAXG', 'AVAX', 'ADA', 'ARB', 'LINK', 'UNI', 'DOGE', 'SPY', 'TQQQ', 'GUSH', 'TMF', 'TNA', 'UGL']);
            setAvailableModels(['GPT-4', 'Claude-3', 'Manual', 'Technical Analysis']);
            setAvailableTimeframes([
                { value: '4h', label: '4 Hours' },
                { value: '24h', label: '24 Hours' },
                { value: '48h', label: '48 Hours' },
                { value: '7d', label: '7 Days' },
                { value: '30d', label: '30 Days' },
                { value: '180d', label: '180 Days' }
            ]);
        }
    };

    const fetchPredictionsByDate = async () => {
        // Ensure we have valid date values before making API call
        if (!filterYear || !filterMonth || !filterDay) {
            console.log('Missing date parameters:', { filterYear, filterMonth, filterDay });
            return;
        }
        
        setLoading(true);
        setError(null);
        
        // Construct the date here to ensure we have current state values
        const currentSelectedDate = `${filterYear}-${filterMonth.padStart(2, '0')}-${filterDay.padStart(2, '0')}`;
        console.log('Fetching predictions for date:', currentSelectedDate, 'type:', type);
        
        try {
            const params = new URLSearchParams();
            params.append('date', currentSelectedDate);
            params.append('date_type', type);
            params.append('limit', '1000'); // Show all for the specific date
            
            // Add additional filters
            if (filterSymbol) params.append('symbol', filterSymbol);
            if (filterModel) params.append('model', filterModel);
            if (filterTimeframe) params.append('timeframe', filterTimeframe);
            
            console.log('API URL:', `${config.api.API_URL}/api/predictions/by-date?${params}`);
            
            const response = await fetch(`${config.api.API_URL}/api/predictions/by-date?${params}`, {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('API Response:', data);
                setPredictions(data.predictions || []);
            } else {
                throw new Error('Failed to fetch predictions');
            }
        } catch (error) {
            console.error('Error fetching predictions by date:', error);
            setError('Failed to load predictions. Please try again.');
            setPredictions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        // Build new URL with all parameters
        const params = new URLSearchParams();
        params.set('type', type);
        if (filterSymbol) params.set('symbol', filterSymbol);
        if (filterModel) params.set('model', filterModel);
        if (filterTimeframe) params.set('timeframe', filterTimeframe);
        
        const newUrl = `/predictions-by-date/${filterYear}/${filterMonth}/${filterDay}?${params.toString()}`;
        navigate(newUrl);
    };

    const handleClearFilters = () => {
        setFilterSymbol('');
        setFilterModel('');
        setFilterTimeframe('');
        
        const params = new URLSearchParams();
        params.set('type', type);
        const newUrl = `/predictions-by-date/${filterYear}/${filterMonth}/${filterDay}?${params.toString()}`;
        navigate(newUrl);
    };

    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push(i);
        }
        return years;
    };

    const generateMonthOptions = () => {
        return [
            { value: '1', label: 'January' },
            { value: '2', label: 'February' },
            { value: '3', label: 'March' },
            { value: '4', label: 'April' },
            { value: '5', label: 'May' },
            { value: '6', label: 'June' },
            { value: '7', label: 'July' },
            { value: '8', label: 'August' },
            { value: '9', label: 'September' },
            { value: '10', label: 'October' },
            { value: '11', label: 'November' },
            { value: '12', label: 'December' }
        ];
    };

    const generateDayOptions = () => {
        const daysInMonth = new Date(parseInt(filterYear), parseInt(filterMonth), 0).getDate();
        const days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        return days;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        let date;
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
            const convertedString = dateString.replace(' ', 'T') + 'Z';
            date = new Date(convertedString);
        } else {
            date = new Date(dateString);
        }
        
        if (isNaN(date.getTime())) return 'Invalid Date';
        
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (prediction) => {
        const now = new Date();
        const validUntil = new Date(prediction.valid_until);
        
        if (prediction.actual_price !== null && prediction.actual_price !== undefined) {
            const inRange = prediction.actual_price >= prediction.range_min && prediction.actual_price <= prediction.range_max;
            return <Badge color={inRange ? "success" : "danger"}>{inRange ? "Success" : "Failed"}</Badge>;
        } else if (validUntil <= now) {
            return <Badge color="warning">Expired</Badge>;
        } else {
            return <Badge color="primary">Active</Badge>;
        }
    };

    const getPerformanceDisplay = (prediction) => {
        if (!prediction.current_price) return '-';
        
        const current = parseFloat(prediction.current_price);
        const actual = parseFloat(prediction.actual_price);
        
        if (actual && actual > 0) {
            const change = ((actual - current) / current * 100);
            const changeClass = change >= 0 ? "text-success" : "text-danger";
            const changeIcon = change >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line";
            
            return (
                <span>
                    ${current.toFixed(2)} → ${actual.toFixed(2)}
                    <br />
                    <small className={changeClass}>
                        <i className={changeIcon}></i> {change.toFixed(2)}%
                    </small>
                </span>
            );
        }
        
        return `$${current.toFixed(2)} → -`;
    };

    document.title = `Predictions ${type === 'created' ? 'Created' : 'Expired'} on ${displayDate}${filterSymbol ? ` - ${filterSymbol}` : ''}${filterModel ? ` - ${filterModel}` : ''} | AI Financial Prediction Tracker`;

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={`Predictions by Date`} pageTitle="Predictions" />
                    <ResearchDisclaimer className="mb-3" />

                    {/* Back to List Button */}
                    <Row className="mb-3">
                        <Col>
                            <Link to="/predictions-list" className="btn btn-secondary">
                                <i className="ri-arrow-left-line me-1"></i>
                                Back to Predictions List
                            </Link>
                        </Col>
                    </Row>

                    {/* Error Alert */}
                    {error && (
                        <Alert color="danger">
                            {error}
                        </Alert>
                    )}

                    <Row>
                        <Col xl={12}>
                            <Card>
                                <PreviewCardHeader title={
                                    `Predictions ${type === 'created' ? 'Created' : 'Expired'} on ${displayDate}`
                                } />
                                <CardBody>
                                    {/* Filter Options Section */}
                                    <Card className="border mb-4">
                                        <CardBody className="bg-light">
                                            <h6 className="mb-3">
                                                <i className="ri-filter-line me-2"></i>
                                                Filter Options
                                            </h6>
                                            <Form>
                                                <Row className="g-3">
                                                    {/* Date Selection */}
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterYear">Year</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterYear"
                                                                value={filterYear}
                                                                onChange={(e) => setFilterYear(e.target.value)}
                                                            >
                                                                {generateYearOptions().map(year => (
                                                                    <option key={year} value={year}>{year}</option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterMonth">Month</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterMonth"
                                                                value={filterMonth}
                                                                onChange={(e) => setFilterMonth(e.target.value)}
                                                            >
                                                                {generateMonthOptions().map(month => (
                                                                    <option key={month.value} value={month.value}>
                                                                        {month.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterDay">Day</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterDay"
                                                                value={filterDay}
                                                                onChange={(e) => setFilterDay(e.target.value)}
                                                            >
                                                                {generateDayOptions().map(day => (
                                                                    <option key={day} value={day}>{day}</option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    
                                                    {/* Symbol Filter */}
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterSymbol">Symbol</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterSymbol"
                                                                value={filterSymbol}
                                                                onChange={(e) => setFilterSymbol(e.target.value)}
                                                            >
                                                                <option value="">All Symbols</option>
                                                                {availableSymbols.map(symbol => (
                                                                    <option key={symbol} value={symbol}>{symbol}</option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    
                                                    {/* Model Filter */}
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterModel">Model/Predictor</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterModel"
                                                                value={filterModel}
                                                                onChange={(e) => setFilterModel(e.target.value)}
                                                            >
                                                                <option value="">All Models</option>
                                                                {availableModels.map(model => (
                                                                    <option key={model} value={model}>{model}</option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    
                                                    {/* Timeframe Filter */}
                                                    <Col md={2}>
                                                        <FormGroup>
                                                            <Label for="filterTimeframe">Timeframe</Label>
                                                            <Input
                                                                type="select"
                                                                id="filterTimeframe"
                                                                value={filterTimeframe}
                                                                onChange={(e) => setFilterTimeframe(e.target.value)}
                                                            >
                                                                <option value="">All Timeframes</option>
                                                                {availableTimeframes.map(timeframe => (
                                                                    <option key={timeframe.value} value={timeframe.value}>
                                                                        {timeframe.label}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                
                                                {/* Action Buttons */}
                                                <Row className="mt-3">
                                                    <Col>
                                                        <div className="d-flex gap-2">
                                                            <Button 
                                                                color="primary" 
                                                                onClick={handleApplyFilters}
                                                            >
                                                                <i className="ri-search-line me-1"></i>
                                                                Apply Filters
                                                            </Button>
                                                            <Button 
                                                                color="secondary" 
                                                                outline
                                                                onClick={handleClearFilters}
                                                            >
                                                                <i className="ri-refresh-line me-1"></i>
                                                                Clear Filters
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        </CardBody>
                                    </Card>

                                    {/* Date Type Selector */}
                                    <Row className="mb-4">
                                        <Col md={6}>
                                            <div className="d-flex gap-2">
                                                <Link 
                                                    to={`/predictions-by-date/${filterYear}/${filterMonth}/${filterDay}?type=created${filterSymbol ? `&symbol=${filterSymbol}` : ''}${filterModel ? `&model=${filterModel}` : ''}${filterTimeframe ? `&timeframe=${filterTimeframe}` : ''}`}
                                                    className={`btn ${type === 'created' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                >
                                                    <i className="ri-add-line me-1"></i>
                                                    Created on this Date
                                                </Link>
                                                <Link 
                                                    to={`/predictions-by-date/${filterYear}/${filterMonth}/${filterDay}?type=expired${filterSymbol ? `&symbol=${filterSymbol}` : ''}${filterModel ? `&model=${filterModel}` : ''}${filterTimeframe ? `&timeframe=${filterTimeframe}` : ''}`}
                                                    className={`btn ${type === 'expired' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                >
                                                    <i className="ri-time-line me-1"></i>
                                                    Expired on this Date
                                                </Link>
                                            </div>
                                        </Col>
                                        <Col md={6} className="text-end">
                                            <Badge color="info" className="px-3 py-2">
                                                Total: {predictions.length} predictions
                                                {(filterSymbol || filterModel || filterTimeframe) && (
                                                    <span className="ms-2">(filtered)</span>
                                                )}
                                            </Badge>
                                        </Col>
                                    </Row>

                                    {/* Loading State */}
                                    {loading && (
                                        <div className="text-center py-5">
                                            <Spinner color="primary" />
                                            <p className="mt-2">Loading predictions...</p>
                                        </div>
                                    )}

                                    {/* No Results */}
                                    {!loading && predictions.length === 0 && (
                                        <div className="text-center py-5">
                                            <i className="ri-calendar-line display-4 text-muted"></i>
                                            <h5 className="mt-3">No predictions found</h5>
                                            <p className="text-muted">
                                                No predictions were {type === 'created' ? 'created' : 'expired'} on {displayDate}
                                                {(filterSymbol || filterModel || filterTimeframe) && ' with the selected filters'}.
                                            </p>
                                            {(filterSymbol || filterModel || filterTimeframe) && (
                                                <Button 
                                                    color="outline-primary" 
                                                    onClick={handleClearFilters}
                                                    className="mt-2"
                                                >
                                                    <i className="ri-refresh-line me-1"></i>
                                                    Clear Filters
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    {/* Predictions Table */}
                                    {!loading && predictions.length > 0 && (
                                        <div className="table-responsive">
                                            <Table className="table-nowrap align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Asset</th>
                                                        <th scope="col">Model/Predictor</th>
                                                        <th scope="col">Created By</th>
                                                        <th scope="col">Price Range</th>
                                                        <th scope="col">Current → Actual</th>
                                                        <th scope="col">Performance</th>
                                                        <th scope="col">Confidence</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">
                                                            {type === 'created' ? 'Created' : 'Expires'}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {predictions.map((prediction) => (
                                                        <tr 
                                                            key={prediction.id}
                                                            onClick={(e) => handlePredictionClick(prediction.id, e)}
                                                            style={{ cursor: 'pointer' }}
                                                            className="table-row-hover"
                                                        >
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="flex-shrink-0 me-3">
                                                                        <div className="avatar-sm bg-light rounded p-1">
                                                                            <span className="fw-semibold">{prediction.symbol}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <h5 className="fs-14 mb-1">{prediction.symbol}</h5>
                                                                        <p className="text-muted mb-0 fs-12">{prediction.prediction_window_days} days</p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <h6 className="fs-14 mb-1">{prediction.model}</h6>
                                                                    <p className="text-muted mb-0 fs-12">
                                                                        <Badge color="secondary" className="badge-soft">
                                                                            {prediction.prediction_type}
                                                                        </Badge>
                                                                    </p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <h6 className="fs-14 mb-1">{prediction.user_name || prediction.created_by || 'Unknown'}</h6>
                                                                    {prediction.user_email && (
                                                                        <p className="text-muted mb-0 fs-12">{prediction.user_email}</p>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="text-center">
                                                                    <span className="d-block fw-semibold text-success">${prediction.range_max}</span>
                                                                    <small className="text-muted">to</small>
                                                                    <span className="d-block fw-semibold text-danger">${prediction.range_min}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {getPerformanceDisplay(prediction)}
                                                            </td>
                                                            <td>
                                                                <div className="text-center">
                                                                    {prediction.score && (
                                                                        <div>
                                                                            <span className="fw-semibold fs-15">{prediction.score}%</span>
                                                                            <br />
                                                                            <small className="text-muted">Final Score</small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="text-center">
                                                                    {prediction.confidence ? (
                                                                        <span className="fw-semibold">{prediction.confidence}%</span>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {getStatusBadge(prediction)}
                                                            </td>
                                                            <td>
                                                                <div className="text-nowrap">
                                                                    {type === 'created' ? 
                                                                        formatDate(prediction.input_time) : 
                                                                        formatDate(prediction.valid_until)
                                                                    }
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default PredictionsByDate;
